/**
 * ionCapabilityRecordings — the capability-gallery's recording registry
 * (Guided Tour Redesign, Part D).
 *
 * Keyed by CAPABILITY ("what Ion can do"), deliberately kept as a SIBLING of
 * ionDemoMode.js's `BRANCH_FIXTURES`, not merged into it:
 *
 *   - `BRANCH_FIXTURES` (ionDemoMode.js) is a sales hedge, gated behind BOTH
 *     a URL flag AND a localStorage opt-in (isDemoActive()) — a trial user
 *     who stumbles onto a `?ion_demo=` URL must still get the real product.
 *     It is keyed by industry branch because that's the sales axis.
 *   - `CAPABILITY_RECORDINGS` here is product surface: reachable by every
 *     user, at any time, from the Help menu / Ion-Home / tour step 3, with
 *     NO gate at all. It is keyed by capability because that's the gallery's
 *     axis — "what Ion can do", not "which industry".
 *
 * Collapsing the two into one registry would make it one accidental edit
 * away from applying the sales gate to the gallery (or the reverse) — kept
 * as separate files/exports, that coupling has to be a deliberate choice,
 * not a side effect of reusing a shared map.
 *
 * `playRecordedFlow()` (hooks/useChatStream.js) is the player; this file
 * only holds the data it plays. `gallery-placeholder` gives the replay
 * mechanism (D1/D2) and its tests something synthetic to run against — it is
 * NOT a capability card and must not be wired into the gallery UI (D3).
 *
 * Real entries are captured with `backend/scripts/capture_ion_recording.py`
 * against staging and REVIEWED before landing here — a weak turn in the
 * shipped gallery is worse than an honest "coming soon". Two things that
 * script's docstring explains and that bit on the first attempt: a steering
 * recording needs `--page cells` (the explorer tools refuse to run without
 * it and Ion answers "navigate there first"), and the fixture must come from
 * `--out`, never a stdout redirect, because the app logs to stdout and the
 * stray lines are silently dropped by the player.
 */

/*
 * Fixtures are loaded with a DYNAMIC `import('…?raw')`, not a static one.
 *
 * Measured, not assumed: the six real captures are ~208 KB of NDJSON, and a
 * static `?raw` import bakes them into `components-*.js` — a 1.46 MB shared
 * chunk that pages load whether or not anyone opens the gallery, i.e. a ~14%
 * penalty on that chunk for a surface most users never touch. (ionDemoMode.js
 * imports its BRANCH_FIXTURES statically; those are small and behind a sales
 * gate, so the same reasoning does not force a change there.)
 *
 * `loadFixture` is a function on EVERY entry including the synthetic
 * placeholder, deliberately: one code path in playRecordedFlow rather than
 * "string or loader, check which", so nobody has to remember the distinction.
 */
export const CAPABILITY_RECORDINGS = {
  'steer-explorer': {
    id: 'steer-explorer',
    prompt: 'Filter to NMC cells above 250 Wh/kg and sort them by peak power.',
    label: 'Steer the Explorer',
    description: 'Ion changes the real UI — filters, sort, view — not just describing it.',
    loadFixture: () =>
      import('../../../mocks/capability-steer-explorer.ndjson?raw').then((m) => m.default),
    // Reviewed: set_context (basis=grav), set_filters (NMC, energy>=250
    // grav) and set_view (sortBy=power desc) all dispatched, and the prose
    // claims exactly that and nothing more.
    model: 'eu.anthropic.claude-sonnet-4-6',
    capturedAt: '2026-07-29T06:39:21Z',
  },
  'plot-it': {
    id: 'plot-it',
    prompt: 'Plot these on a Ragone chart so I can see the energy-power trade-off.',
    label: 'Plot it',
    description: 'Turns a filtered set into a Ragone chart.',
    loadFixture: () =>
      import('../../../mocks/capability-plot-it.ndjson?raw').then((m) => m.default),
    // Reviewed: plot_scatter returns a real plotly_chart artifact with the
    // cells grouped by chemistry, and the prose reads the chart it just made
    // (which chemistry sits where, named outliers) rather than describing
    // Ragone plots in general.
    model: 'eu.anthropic.claude-sonnet-4-6',
    capturedAt: '2026-07-29T06:49:05Z',
  },
  'compare-cells': {
    id: 'compare-cells',
    prompt: 'Compare the Molicel P42A and the Samsung INR21700-50E side by side.',
    label: 'Compare cells',
    description: 'A side-by-side spec table with best/worst marking.',
    loadFixture: () =>
      import('../../../mocks/capability-compare-cells.ndjson?raw').then((m) => m.default),
    // Reviewed: search_cells resolves both cells, compare_cells_table emits
    // the compare_table artifact, and the prose leads with the actual finding
    // ("the P42A is a power cell; the 50E is an energy cell") instead of
    // reciting the table.
    model: 'eu.anthropic.claude-sonnet-4-6',
    capturedAt: '2026-07-29T06:50:15Z',
  },
  'explain-concept': {
    id: 'explain-concept',
    prompt:
      'What does a Ragone plot show, and where do the Molicel P42A and the ' +
      'Samsung INR21700-50E sit on it? Use their real figures.',
    label: 'Explain a concept',
    description: 'A sourced answer, every figure traceable.',
    loadFixture: () =>
      import('../../../mocks/capability-explain-concept.ndjson?raw').then((m) => m.default),
    // RE-CAPTURED on purpose. The first attempt asked the concept question
    // alone: Ion answered in pure prose with ZERO tool calls, and the
    // value-guard flagged three figures as unverified — a recording that
    // contradicted its own card ("every figure traceable") on the one axis
    // the card sells. Anchoring the question in two real cells forces the
    // lookup: search_cells x2 + plot_scatter, and no ion_unverified warnings
    // in the capture log, so every number carries a tool result behind it.
    model: 'eu.anthropic.claude-sonnet-4-6',
    capturedAt: '2026-07-29T06:54:17Z',
  },
  'guided-selection': {
    id: 'guided-selection',
    prompt: 'I am choosing a cell for an eBike battery pack.',
    label: 'Guided selection',
    description: 'Requirement-driven narrowing across branches.',
    loadFixture: () =>
      import('../../../mocks/capability-guided-selection.ndjson?raw').then((m) => m.default),
    // Reviewed: apply_application_profile loads the eBike profile and
    // ask_user opens the first requirement question. Deliberately short — it
    // shows the flow STARTING, which is the capability. Note the recording
    // ends on a question: answering it starts a real (charged) turn, which is
    // a reasonable hand-off, not a bug.
    model: 'eu.anthropic.claude-sonnet-4-6',
    capturedAt: '2026-07-29T06:50:56Z',
  },
  'build-report': {
    id: 'build-report',
    prompt: 'Build me a report on the best 21700 cells for a power-tool pack.',
    label: 'Build a report',
    // FB-60 (MS): "Du kündigst an, man könne sich einen Report runterladen. Das
    // geht aber nicht." It read "Findings as an exportable, downloadable
    // document", which is a promise the surface it advertises cannot keep — a PDF
    // export exists ONLY in the guided-selection Report tab and only once a
    // shortlist exists (CanvasHeader gates it on stage). Someone reading this
    // card on the Ion home asked Ion for a PDF and was told there is none.
    // The description now says what the recording actually shows.
    description: 'Charts, comparisons and written findings in one answer.',
    loadFixture: () =>
      import('../../../mocks/capability-build-report.ndjson?raw').then((m) => m.default),
    // The longest capture by far (471 lines): get_knowledge, search_cells,
    // two plot_bar, plot_scatter and compare_cells_table, then ~4.8k
    // characters of findings.
    //
    // KNOWN WART, kept faithful: the turn ends with a `compare_cells` tool
    // whose output never streamed — 7 tool-input-starts against 6
    // tool-output-availables. That is what the real turn did, so a live user
    // sees the same thing; it is a backend/stream quirk, not a fixture edit
    // waiting to happen. Verified visually that it does not leave a chip
    // spinning before shipping.
    model: 'eu.anthropic.claude-sonnet-4-6',
    capturedAt: '2026-07-29T06:51:56Z',
  },
  'gallery-placeholder': {
    id: 'gallery-placeholder',
    // The recorded user question, appended verbatim as the user message.
    // Recordings are in English (BA decision 3), matching the interface.
    prompt: 'Show me 21700 cells above 250 Wh/kg, sorted by peak power.',
    label: 'Steer the Explorer (placeholder)',
    description:
      'Synthetic recording for the replay mechanism and its tests only — ' +
      'not a capability card, never rendered in the gallery.',
    loadFixture: () =>
      import('../../../mocks/capability-gallery-placeholder.ndjson?raw').then((m) => m.default),
    // Every shipped recording notes the model id and capture date, so a
    // recording made under an older system prompt can be spotted when Ion's
    // voice drifts. This one was never captured from a real turn —
    // synthetic, for tests only — so both are null.
    model: null,
    capturedAt: null,
  },
};

/**
 * GALLERY_CARD_ORDER / CAPABILITY_GALLERY_CARDS — the six planned capability
 * cards (Part D3), in the order the spec asks them AUTHORED (D4): the three a
 * first-time user cares about most, then the two most expensive to record.
 *
 * This is the ONLY hardcoded list the gallery UI (IonCapabilityGallery.jsx)
 * uses. A card's AVAILABILITY is never tracked here or in a second list —
 * it's derived by looking up the card's `id` in CAPABILITY_RECORDINGS above,
 * so a card can never claim "available" without an actual recording behind
 * it. As of 2026-07-29 exactly ONE of the six resolves — `steer-explorer`,
 * captured from a real staging turn — so that card offers Play and the other
 * five still render honestly as "Recording coming soon". (`gallery-placeholder`
 * is not one of the six and never renders; see its own comment.)
 *
 * D4 must register its recordings under THESE SAME ids (`steer-explorer`,
 * `plot-it`, `compare-cells`, `explain-concept`, `guided-selection`,
 * `build-report`) — chosen here as the obvious slug for each capability in
 * the spec's own table (Part D). If D4 picks different ids, update this
 * list to match rather than adding a second id namespace.
 */
const GALLERY_CARD_ORDER = [
  'steer-explorer',
  'plot-it',
  'compare-cells',
  'explain-concept',
  'guided-selection',
  'build-report',
];

/**
 * DERIVED from CAPABILITY_RECORDINGS, not a second copy of it. Every card used
 * to repeat its recording's label and description verbatim, which is how a card
 * can promise something the recording never shows: FB-60's "exportable,
 * downloadable document" had to be corrected in both places, and correcting one
 * would have shipped a card and a recording that disagree. This list now owns
 * the ORDER and nothing else.
 */
export const CAPABILITY_GALLERY_CARDS = GALLERY_CARD_ORDER.map((id) => {
  const rec = CAPABILITY_RECORDINGS[id];
  return { id, label: rec.label, description: rec.description };
});

// Exported for tests.
export const __test__ = { CAPABILITY_RECORDINGS };
